
import { convertSchemaToHtml } from "@thebeyondgroup/shopify-rich-text-renderer";
import { evolve, isEmpty, mapObjIndexed } from "ramda";
import { mapState } from "vuex";

import { PAGE_TYPE, PRODUCT_DETAIL_PAGE_QUERY } from "./-index.query";
import getProduct, {
  getTilesInfo,
  getSeoData,
  getVariantsData,
  getVariantTitle,
  isMattressAndBaseRemoval,
} from "~/utils/shopify-utils";
import {
  seoMixin,
  saleTagMixin,
  gtmEventMixin,
  tagableMixin,
  pageMixin,
} from "~/mixins";

const UID = "product-details";

export default {
  mixins: [saleTagMixin, seoMixin, tagableMixin, gtmEventMixin, pageMixin],

  transition: "fade",

  async asyncData({
    app,
    query,
    error,
    redirect,
    store,
    route: { name, params },
    $getShopifyData,
  }) {
    const { handle } = params;

    const product = await getProduct(handle, query.variant, $getShopifyData);
    if (product == null) {
      return error({ statusCode: 404, message: "Page not found" });
    }

    // if wrong variantId is provided then we have to redirect to
    // this pdp without query parameter
    // this happens when searchspring sync is not right
    if (isEmpty(product.selectedVariant)) {
      return redirect({ name, params });
    }

    const blogTags = product?.tags?.filter?.(tag => tag.startsWith("Blog_"));

    // Prismic PDP CMS document + Yotpo reviews fetch in parallel once product.id is known.
    const [{ [PAGE_TYPE]: data }] = await Promise.all([
      app.$prismicGql.request({
        query: PRODUCT_DETAIL_PAGE_QUERY,
        variables: {
          uid: UID,
          hasBlogTags: blogTags.length !== 0,
          lazy: false,
        },
      }),
      store.dispatch("pdp/getReviewRating", { productId: product.id }),
    ]);

    // prepare meta values here
    const metaFields = {
      _linkType: data._linkType,
      _meta: { uid: handle, type: data?._meta?.type },
    };
    const { metaTitle, metaDescription } = getSeoData(product);

    const variantImages = product.selectedVariant.images || [];
    const productImages = product.images || [];
    const images = variantImages.length > 0 ? variantImages : productImages;

    const firstImage = images.length > 0 ? images[0] : null;
    const desktopPreloadUrl = firstImage?.w_1024_h_767_webp ?? null;
    const mobilePreloadUrl =
      firstImage?.w_640_h_479_webp ?? desktopPreloadUrl ?? null;

    /** Responsive preloads avoid downloading the desktop-sized asset on phones when a smaller derivative exists */
    const preloadLinks = [];
    if (desktopPreloadUrl && mobilePreloadUrl !== desktopPreloadUrl) {
      preloadLinks.push(
        {
          hid: "pdp-preload-hero-desktop",
          rel: "preload",
          href: desktopPreloadUrl,
          as: "image",
          type: "image/webp",
          fetchpriority: "high",
          media: "(min-width: 1024px)",
        },
        {
          hid: "pdp-preload-hero-mobile",
          rel: "preload",
          href: mobilePreloadUrl,
          as: "image",
          type: "image/webp",
          fetchpriority: "high",
          media: "(max-width: 1023px)",
        }
      );
    } else if (desktopPreloadUrl) {
      preloadLinks.push({
        hid: "pdp-preload-hero",
        rel: "preload",
        href: desktopPreloadUrl,
        as: "image",
        type: "image/webp",
        fetchpriority: "high",
      });
    }

    return {
      handle,
      slices: data.slices,
      title: data.title,
      product,
      currentVariantId: product.selectedVariant.id,
      selectedVariant: product.selectedVariant,
      metaRobots: data.metaRobots,
      metaFields,
      metaTitle,
      metaDescription,
      paymentIcons: data.payment_icon_document,
      comfortLayer: data.comfort_layer,
      storeContent: data.find_a_store_content,
      blogTags,
      uid: UID,
      pageType: PAGE_TYPE,
      addToCartStyles: {
        color: data.add_to_cart_color,
        backgroundColor: data.add_to_cart_bg_color,
        fontSize: `${data.add_to_cart_font}px`,
      },
      addToCartText: data.add_to_cart_text,
      antiTippingBanner: data.anti_tipping_banner,
      inStoreColorsText: data.instore_colors_text,
      cmsData: data,
      additionalLinks: preloadLinks,
    };
  },

  computed: {
    ...mapState(["siteConfig"]),
    ...mapState("app", ["quickviewProduct", "addedToBagVariants"]),
    pageTitle() {
      return [{ text: this.product.title }];
    },
    productTitle() {
      const variantTitle = getVariantTitle({
        variant: this.selectedVariant,
        isMultiVariant: this.isMultiVariant,
      });
      return `${this.product.title}${variantTitle}`;
    },
    addToCartOffset() {
      return this.siteConfig.headerHeight / 2;
    },
    variantTiles() {
      const tilesInfo = getTilesInfo(
        this.product.options,
        this.product.variants
      );
      const selectedVarOptions = mapObjIndexed(
        x => x?.value,
        this.selectedVariant.selectedOptions
      );

      return tilesInfo(selectedVarOptions).filter(tile => tile?.selectedTile);
    },
    colorVariantTile() {
      return this.variantTiles.find(
        variantTile => variantTile?.label == "Colour"
      );
    },
    allImages() {
      const variantImages = this.selectedVariant?.images || [];
      const productImages = this.product?.mediaImages || [];

      // If variant has images, show only variant images.
      // Otherwise, fall back to product-level media images.
      const images = variantImages.length > 0 ? variantImages : productImages;

      return images.map(evolve({ altText: x => x || this.imageAltText }));
    },
    imageProps() {
      return {
        images: this.allImages,
        threeDUrl: this.product.three_d_url,
        sku: this.selectedVariant.sku,
        tags: this.product.tags,
        imageAlt: this.imageAltText,
      };
    },
    variantImageSrc() {
      return this.allImages?.[0]?.w_1024_h_767_webp;
    },
    imageAltText() {
      return `${this.product.title} - ${this.selectedVariant.title}`;
    },
    tags() {
      return this.product?.tags ?? [];
    },
    hasBespokeCustomisation() {
      return Boolean(this.selectedVariant?.bespoke_sku);
    },
    dataGalleryId() {
      return this.siteConfig?.pdp?.dataGalleryId;
    },
    dotDigitalProduct() {
      const { price, sku } = this.selectedVariant;

      return {
        product_name: this.product.title,
        product_sku: sku,
        product_price: price,
        product_url: `https://${this.$config.shopifyCheckoutDomain}/products/${this.product.handle}`,
        product_image_path: this.allImages[0]?.src,
        ...(this.currentCampaigns.length && {
          product_specialPrice: price * (1 - this.maxDiscount / 100),
        }),
        product_status: "In stock",
        product_categories: this.product.collections ?? "",
        product_description: this.product.description,
        product_currency: "AUD",
        product_brand: this.product.brand_list ?? "",
      };
    },
    sliceContext() {
      return {
        productId: this.product.id,
        sku: this.selectedVariant.sku,
        selectedVariant: this.selectedVariant,
        hydrateSlicesEagerly: true,
      };
    },
    assemblyInstructionsHtml() {
      const raw = this.product?.assemblyInstructions?.value;

      if (!raw || typeof raw !== "string") {
        return "";
      }

      try {
        const richTextJson = JSON.parse(raw);
        let html = convertSchemaToHtml(richTextJson);

        // Ensure all links open in a new tab
        html = html.replace(
          /<a(?![^>]*target=)/g,
          '<a target="_blank" rel="noopener noreferrer"'
        );

        return html;
      } catch (err) {
        this.$bugsnag?.notify(err, event => {
          event.addMetadata("assemblyInstructions", {
            metafield: "snooze.assembly_instructions",
            productHandle: this.product?.handle,
            rawValue: raw,
          });
        });

        return "";
      }
    },
    showMattressAndBaseRemoval() {
      return isMattressAndBaseRemoval(this.tags);
    },
  },

  watch: {
    currentVariantId: async function handleProductChange(variantId) {
      this.$nuxt.$loading.start();
      this.$store.dispatch("pdp/setVariantLoading");
      // fetch the variant data and set to the selectedVariant
      this.selectedVariant = await getVariantsData(
        variantId,
        this.$getShopifyData
      );
      this.$store.dispatch("pdp/resetVariantLoading");
      if (this.isMultiVariant || this.oneProductMultipleFeels) {
        // change handle and query parameter
        this.$router.replace({
          params: { handle: this.selectedVariant.product.handle },
          query: { variant: this.selectedVariant.id },
        });
      } else {
        // change the query parameter
        this.$router.replace({ query: { variant: this.selectedVariant.id } });
      }
      setTimeout(() => this.$nuxt.$loading.finish(), 500);
    },
  },

  created() {
    this.getSaleTag({
      tags: this.tags,
      campaign: this.product.campaign,
      price: this.selectedVariant.price,
      compareAtPrice: this.selectedVariant.compareAtPrice,
    });
  },

  mounted() {
    this.gtmPageView();

    this.$search.setViewedProducts(this.selectedVariant.sku);

    this.$searchspring.trackProductView(
      this.product.id,
      this.selectedVariant?.id ?? this.product.id,
      this.selectedVariant?.sku
    );
  },

  destroyed() {
    if (this.addedToBagVariants.length) {
      this.$store.dispatch("app/closeAddedToBag", this.isTablet);
    }
  },

  methods: {
    handleVariantSelect(variantId, label, tileLabel) {
      this.currentVariantId = variantId;
    },
    gtmPageView() {
      this.$gtm.gtagEvent("view_item", {
        value: this.selectedVariant.price,
        items: [
          {
            item_id: this.product.id,
            item_sku: this.selectedVariant.sku,
            item_name: this.product.title,
            currency: "AUD",
            discount:
              this.selectedVariant.compareAtPrice > this.selectedVariant.price
                ? this.selectedVariant.compareAtPrice -
                  this.selectedVariant.price
                : 0,
            item_brand: this.product?.brand_list ?? "",
            item_category: this.product?.product_category ?? "",
            item_variant: this.selectedVariant.title,
            price: this.selectedVariant.price,
            quantity: 1,
          },
        ],
      });
    },
  },
};
